import React, { useEffect, useState } from "react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";

import {
    Box,
    Modal,
    Typography,
    TextField,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Divider,
    Select,
    Card,
    Radio,
    FormControlLabel,
    RadioGroup,
    Chip,
    ListSubheader,
    Checkbox,
    Grid,
    ListItemText,
    List,
    Icon,
    FormHelperText,
    CircularProgress,
    Autocomplete,
} from "@mui/material";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

export function ActivitiesEdit({
    open,
    handleClose,
    isDisabled,
    getFieldValue,
    handleFieldChange,
    handleSubmit,
    isFieldValid,
    listSubCategories = [],
    listNodes = [],
    selectedSubcategories,
    setSelectedSubcategories,
    hasChanges = false,
    isSubmitting = false,
}) {
    const style = {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "90%",
        maxWidth: "400px",
        borderRadius: "20px",
        boxShadow: 24,
        p: 4,
    };

    const maxLength = 550;

    useEffect(() => {
        // Sincronizar subcategorías cuando cambie selectedSubcategories desde el padre
        if (open) {
            const subcategoriesFromParent = getFieldValue("subcategories") || [];
            if (Array.isArray(subcategoriesFromParent) &&
                JSON.stringify(subcategoriesFromParent) !== JSON.stringify(selectedSubcategories)) {
                setSelectedSubcategories(subcategoriesFromParent);
            }
        }
    }, [open, selectedSubcategories, getFieldValue]);

    const handleChange = (event) => {
        const newValue = event.target.value;
        setSelectedSubcategories(newValue);
        handleFieldChange("subcategories", newValue);
    };

    const renderSelectGroup = (category) => {
        const items = category.subcategories.map((sub) => (
            <MenuItem key={sub.id} value={sub.id}>
                <Checkbox checked={selectedSubcategories.indexOf(sub.id) > -1} />
                <ListItemText primary={sub.name} />
            </MenuItem>
        ));
        return [<ListSubheader key={category.id}>{category.name}</ListSubheader>, items];
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description">
            <Card sx={style}>
                <MDBox
                    mt={-7}
                    color="white"
                    bgColor="info"
                    variant="gradient"
                    borderRadius="lg"
                    p={2}
                    sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Typography id="modal-modal-title" component="h2">
                        Editar actividad
                    </Typography>
                    <Icon
                        sx={{
                            fontSize: "2em",
                            stroke: "currentColor",
                            strokeWidth: "2px",
                            cursor: "pointer",
                            marginLeft: "auto",
                        }}
                        onClick={handleClose}>
                        close
                    </Icon>
                </MDBox>
                <Divider variant="middle" />
                <form onSubmit={handleSubmit}>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Fecha"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            name="fecha"
                            value={getFieldValue("fecha")}
                            error={!isFieldValid.fecha}
                            helperText={
                                getFieldValue("fecha") === "" && !isFieldValid.fecha
                                    ? "El campo no puede estar vacío"
                                    : !isFieldValid.fecha
                                        ? "El campo solo acepta formato de fecha"
                                        : ""
                            }
                            FormHelperTextProps={{ className: !isFieldValid.fecha ? "Mui-error" : "" }}
                            onChange={(e) => handleFieldChange("fecha", e.target.value)}
                            required
                            disabled={isDisabled}
                        />
                    </Box>
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="time"
                                    label="Hora Inicio"
                                    value={getFieldValue("timeStart")}
                                    onChange={(e) => handleFieldChange("timeStart", e.target.value)}
                                    error={!isFieldValid.timeStart}
                                    helperText={
                                        !isFieldValid.timeStart ? "Asegurate de que las horas sean correctas" : ""
                                    }
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        step: 300,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="time"
                                    label="Hora Fin"
                                    value={getFieldValue("timeEnd")}
                                    onChange={(e) => handleFieldChange("timeEnd", e.target.value)}
                                    error={!isFieldValid.timeEnd}
                                    helperText={
                                        !isFieldValid.timeEnd ? "Asegurate de que las horas sean correctas" : ""
                                    }
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        step: 300,
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Título"
                            name="title"
                            value={getFieldValue("title")}
                            error={!isFieldValid.title}
                            helperText={
                                getFieldValue("title") === "" && !isFieldValid.title
                                    ? "El campo no puede estar vacío"
                                    : !isFieldValid.title
                                        ? "No se permiten caracteres especiales"
                                        : ""
                            }
                            FormHelperTextProps={{ className: !isFieldValid.title ? "Mui-error" : "" }}
                            onChange={(e) => handleFieldChange("title", e.target.value)}
                            required
                            disabled={isDisabled}
                        />
                    </Box>
                    <Box mb={2}>
                        <FormControl fullWidth error={!isFieldValid.subcategories}>
                            <InputLabel htmlFor="subcategorySelect">Seleccionar subcategorías *</InputLabel>
                            <Select
                                multiple
                                value={selectedSubcategories}
                                label="Seleccionar subcategorías *"
                                onChange={handleChange}
                                required
                                renderValue={(selected) => (
                                    <div>
                                        {selected
                                            .map((id) => {
                                                const subcategory = listSubCategories
                                                    .flatMap((c) => c.subcategories)
                                                    .find((s) => s.id === id);
                                                return subcategory ? subcategory.name : null;
                                            })
                                            .join(", ")}
                                    </div>
                                )}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 300, // Limita la altura del menú
                                            width: "auto", // Ajusta el ancho dinámicamente según el contenido
                                            minWidth: 250, // Define un ancho mínimo para evitar que sea demasiado pequeño
                                            whiteSpace: "normal", // Permite que las palabras largas se ajusten en varias líneas
                                        },
                                    },
                                }}
                                sx={{
                                    width: "100%", // Ajusta el ancho del Select
                                    height: "42px", // Aumenta la altura del Select
                                }}>
                                {/* Renderiza las opciones de subcategorías */}
                                {listSubCategories.map((c) => renderSelectGroup(c))}
                            </Select>
                            {selectedSubcategories?.length < 1 && !isFieldValid.subcategories && (
                                <FormHelperText>Subcategoría requerida</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Descripción"
                            name="description"
                            value={getFieldValue("description")}
                            error={!isFieldValid.description}
                            helperText={
                                getFieldValue("description") === "" && !isFieldValid.description
                                    ? "El campo no puede estar vacío"
                                    : !isFieldValid.description
                                        ? "No se permiten caracteres especiales"
                                        : "" + ` ${getFieldValue("description")?.length || 0} / ${maxLength} caracteres`
                            }
                            FormHelperTextProps={{ className: !isFieldValid.description ? "Mui-error" : "" }}
                            onChange={(e) => handleFieldChange("description", e.target.value)}
                            required
                            disabled={isDisabled}
                            multiline
                            rows={2}
                            inputProps={{
                                maxLength: maxLength,
                            }}
                        />
                    </Box>
                    <Box mb={2}>
                        <TextField
                            fullWidth
                            label="Palabras clave"
                            name="keywords"
                            value={getFieldValue("keywords")}
                            error={!isFieldValid.keywords}
                            helperText={
                                getFieldValue("keywords") === "" && !isFieldValid.keywords
                                    ? "El campo no puede estar vacío"
                                    : !isFieldValid.keywords
                                        ? "No se permiten caracteres especiales"
                                        : "" + ` ${getFieldValue("keywords")?.length || 0} / ${maxLength} caracteres`
                            }
                            FormHelperTextProps={{ className: !isFieldValid.keywords ? "Mui-error" : "" }}
                            onChange={(e) => handleFieldChange("keywords", e.target.value, e)}
                            required
                            disabled={isDisabled}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip
                                            title={
                                                <span>
                                                    Ingresa palabras separadas por espacios que describan la actividad.
                                                    <br />
                                                    Ejemplo: <b>INNOVACIÓN TECNOLOGÍA EDUCACIÓN</b>
                                                    <br />
                                                    Solo letras y espacios, máximo 550 caracteres.
                                                </span>
                                            }
                                            arrow
                                            placement="top">
                                            <IconButton size="small" tabIndex={-1}>
                                                <HelpOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box mb={2}>
                        <FormControl fullWidth error={!isFieldValid.node_id}>
                            <Autocomplete
                                disablePortal
                                options={listNodes || []}
                                getOptionLabel={(option) =>
                                    typeof option === "string" ? option : `${option.name} - ${option.municipality}`
                                }
                                noOptionsText={"No hay resultados"}
                                value={
                                    listNodes && getFieldValue("node_id")
                                        ? listNodes.find((n) => n.id === getFieldValue("node_id")) || null
                                        : null
                                }
                                onChange={(event, value) => {
                                    handleFieldChange("node_id", value ? value.id : "");
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    option && value && option.id === value.id
                                }
                                disabled={isDisabled}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Seleccionar Nodo "
                                        required
                                        InputProps={{
                                            ...params.InputProps,
                                            sx: { height: 42 },
                                        }}
                                    />
                                )}
                                sx={{ width: "100%" }}
                            />
                            {!getFieldValue("node_id") && !isFieldValid.node_id && (
                                <FormHelperText>Nodo requerido</FormHelperText>
                            )}
                        </FormControl>
                    </Box>
                    <Box mb={2}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <InputLabel sx={{ position: "relative", transform: "none", fontSize: "1rem", color: "rgba(0, 0, 0, 0.6)" }}>
                                Certificable *
                            </InputLabel>
                            <FormControl
                                component="fieldset"
                                required
                                error={!isFieldValid.is_certifiable}>
                                <RadioGroup
                                    name="is_certifiable"
                                    value={
                                        getFieldValue("is_certifiable") === null
                                            ? ""
                                            : getFieldValue("is_certifiable")
                                                ? "true"
                                                : "false"
                                    }
                                    onChange={(e) => handleFieldChange("is_certifiable", e.target.value === "true")}
                                    disabled={isDisabled}
                                    row>
                                    <FormControlLabel value="true" control={<Radio />} label="Sí" />
                                    <FormControlLabel value="false" control={<Radio />} label="No" />
                                </RadioGroup>
                                {!isFieldValid.is_certifiable && (
                                    <FormHelperText>Certificable es requerido</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>
                    <Divider variant="middle" />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mt: 2,
                        }}>
                        <MDButton
                            type="submit"
                            color="success"
                            sx={{
                                flex: 1,
                                mr: 1,
                            }}
                            disabled={isDisabled || !hasChanges || isSubmitting}>
                            {isSubmitting ? (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    Actualizando...
                                </Box>
                            ) : (
                                "Actualizar"
                            )}
                        </MDButton>
                        <MDButton
                            onClick={handleClose}
                            color="error"
                            sx={{
                                flex: 1,
                                mr: 1,
                            }}
                            disabled={isDisabled}>
                            Cancelar
                        </MDButton>
                    </Box>
                </form>
            </Card>
        </Modal>
    );
}

ActivitiesEdit.propTypes = {
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    getFieldValue: PropTypes.func,
    handleFieldChange: PropTypes.func,
    handleSubmit: PropTypes.func,
    isFieldValid: PropTypes.shape({
        categories: PropTypes.bool,
        title: PropTypes.bool,
        description: PropTypes.bool,
        is_certifiable: PropTypes.bool,
        timeStart: PropTypes.bool,
        timeEnd: PropTypes.bool,
        node_id: PropTypes.bool,
    }),
    fetchSubCategories: PropTypes.array,
    listNodes: PropTypes.array,
    selectedSubcategories: PropTypes.array,
    setSelectedSubcategories: PropTypes.func,
    hasChanges: PropTypes.bool,
    isSubmitting: PropTypes.bool,
};
